import React, { useState } from "react";
import { toast } from "sonner";
import { GripVertical, ChevronUp, ChevronDown, Pencil, Copy, Trash2, Eye, EyeOff, Star, Link2 } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { http, formatApiError, TYPE_META } from "../resources/resourceUtils";

const IconBtn = ({ label, onClick, children, testid }) => (
    <button type="button" onClick={onClick} aria-label={label} title={label} className="p-1.5 sm:p-2 rounded-lg hover:bg-[#F1EFE8]" data-testid={testid}>
        {children}
    </button>
);

const StatusPill = ({ status }) => (
    <span className="text-[13px] px-2.5 py-1 rounded-full" style={status === "published" ? { background: "#E7EBDC", color: "#46502F" } : { background: "#EFEAEF", color: "#5A4A5A" }}>
        {status === "published" ? "Published" : "Draft"}
    </span>
);

const move = (list, from, to) => {
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

export const ResourceTable = ({ rows, catName, onEdit, onChanged }) => {
    const [dragIndex, setDragIndex] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const act = async (fn, msg) => {
        try { await fn(); if (msg) toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const copyLink = async (r) => {
        const url = `${window.location.origin}/resources/${r.slug}`;
        try { await navigator.clipboard.writeText(url); } catch (e) { window.prompt("Copy this link:", url); return; }
        toast.success(r.status === "published" ? "Link copied" : "Link copied. It will work once this resource is published.", { description: url });
    };
    const reorder = (next) => act(() => http.post("/admin/resources/reorder", { ids: next.map((r) => r.id) }), "Order saved");

    if (rows.length === 0) {
        return <p className="py-16 text-center text-[16px]" style={{ color: "#5C5C47" }} data-testid="admin-resources-empty">No resources here yet.</p>;
    }
    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-[15px]" data-testid="admin-resources-table">
                    <thead>
                        <tr style={{ color: "#5C5C47" }} className="border-b" >
                            <th className="py-3 pl-0 sm:pl-2 w-8 sm:w-10"><span className="sr-only">Order</span></th>
                            <th className="py-3 font-medium">Title</th>
                            <th className="py-3 font-medium hidden md:table-cell">Category</th>
                            <th className="py-3 font-medium hidden sm:table-cell">Type</th>
                            <th className="py-3 font-medium">Status</th>
                            <th className="py-3 font-medium hidden lg:table-cell">Added</th>
                            <th className="py-3 font-medium text-right pr-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr
                                key={r.id}
                                draggable
                                onDragStart={() => setDragIndex(i)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => { if (dragIndex !== null && dragIndex !== i) reorder(move(rows, dragIndex, i)); setDragIndex(null); }}
                                className={`border-b align-middle ${dragIndex === i ? "opacity-50" : ""}`}
                                style={{ borderColor: "#EEEBE3" }}
                                data-testid={`admin-resource-row-${r.id}`}
                            >
                                <td className="py-3 pl-0 sm:pl-2">
                                    <div className="flex items-center" style={{ color: "#8A8A73" }}>
                                        <GripVertical size={16} className="cursor-grab hidden sm:block" aria-hidden />
                                        <div className="flex flex-col">
                                            <button type="button" aria-label={`Move ${r.title} up`} disabled={i === 0} onClick={() => reorder(move(rows, i, i - 1))} className="p-1 disabled:opacity-30" data-testid={`admin-resource-up-${r.id}`}><ChevronUp size={16} /></button>
                                            <button type="button" aria-label={`Move ${r.title} down`} disabled={i === rows.length - 1} onClick={() => reorder(move(rows, i, i + 1))} className="p-1 disabled:opacity-30" data-testid={`admin-resource-down-${r.id}`}><ChevronDown size={16} /></button>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 pr-2 break-words">
                                    <span className="font-medium" style={{ color: "#4A4A30" }} data-testid={`admin-resource-title-${r.id}`}>{r.title}</span>
                                    {r.featured && <Star size={13} className="inline ml-2 -mt-0.5" style={{ color: "#CFA969" }} aria-label="Featured" />}
                                </td>
                                <td className="py-3 hidden md:table-cell">{catName[r.category_id] || "None"}</td>
                                <td className="py-3 hidden sm:table-cell">{TYPE_META[r.type]?.label}</td>
                                <td className="py-3" data-testid={`admin-resource-status-${r.id}`}><StatusPill status={r.status} /></td>
                                <td className="py-3 hidden lg:table-cell">{new Date(r.created_at).toLocaleDateString()}</td>
                                <td className="py-3 pr-0 sm:pr-2">
                                    <div className="flex justify-end gap-0" style={{ color: "#4A4A30" }}>
                                        <IconBtn label={r.status === "published" ? "Unpublish" : "Publish"} testid={`admin-resource-toggle-${r.id}`}
                                            onClick={() => act(() => http.post(`/admin/resources/${r.id}/status`, { status: r.status === "published" ? "draft" : "published" }), r.status === "published" ? "Moved to drafts" : "Published")}>
                                            {r.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </IconBtn>
                                        <IconBtn label="Copy share link" onClick={() => copyLink(r)} testid={`admin-resource-copy-link-${r.id}`}><Link2 size={16} /></IconBtn>
                                        <IconBtn label="Edit" onClick={() => onEdit(r)} testid={`admin-resource-edit-${r.id}`}><Pencil size={16} /></IconBtn>
                                        <IconBtn label="Duplicate" onClick={() => act(() => http.post(`/admin/resources/${r.id}/duplicate`), "Copy added as a draft")} testid={`admin-resource-duplicate-${r.id}`}><Copy size={16} /></IconBtn>
                                        <IconBtn label="Delete" onClick={() => setConfirm(r)} testid={`admin-resource-delete-${r.id}`}><Trash2 size={16} style={{ color: "#9A4557" }} /></IconBtn>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent data-testid="admin-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-[26px] font-normal">Delete this resource?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[15px]">"{confirm?.title}" will be removed from the library. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-delete-cancel">Keep it</AlertDialogCancel>
                        <AlertDialogAction className="bg-[#9A4557] hover:bg-[#7F3747]" data-testid="admin-delete-confirm-button"
                            onClick={() => { const r = confirm; setConfirm(null); act(() => http.delete(`/admin/resources/${r.id}`), "Resource deleted"); }}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
